import React, { useMemo } from 'react'
import {
  FormCheckboxField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { BonusCampaignCountryDto } from 'api/Wallet/Types/MarketingBonusCampaignDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'

import { TickmillCompanyDto } from '../../../api/Identity/Types/Company'
import { CountryDto } from '../../../api/Identity/Types/Country'

export interface AddBonusCountryFormValues {
  tickmillCompanyId: number
  minimumAmount: number
  isActive: boolean
  countryIds: string[]
}

const AddBonusCountryFormUI: React.FC<FormikProps<AddBonusCountryFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setFieldValue, setSubmitting, isSubmitting, errors, values, onClose } =
    props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const usedCountryIds = useMemo(() => {
    if (!props.existingCountryOptions || values.tickmillCompanyId === 0) {
      return new Set<string>()
    }

    return new Set(
      props.existingCountryOptions
        .filter((x) => x.tickmillCompany.id === values.tickmillCompanyId)
        .flatMap((x) => x.countries.map((c) => c.id))
    )
  }, [props.existingCountryOptions, values.tickmillCompanyId])

  const availableCountries = useMemo(() => {
    return props.countries.filter((country) => !usedCountryIds.has(country.id))
  }, [props.countries, usedCountryIds])

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <Form>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Country Option</DialogTitle>
          </DialogHeader>
          <FormField
            name='minimumAmount'
            label='Minimum Amount'
            type='number'
            min='0'
            step='0.01'
            error={errors.minimumAmount}
          />
          <FormSelectField
            name='tickmillCompanyId'
            label='Tickmill Company'
            value={values.tickmillCompanyId || undefined}
            options={props.tickmillCompanies.map((company) => ({
              id: company.id,
              name: company.name,
            }))}
            error={errors.tickmillCompanyId}
            onChange={(value) => {
              setFieldValue('tickmillCompanyId', Number(value))
              setFieldValue('countryIds', [])
            }}
          />
          <FormMultiSelectField
            name='countryIds'
            label='Countries'
            placeholder='Countries'
            value={values.countryIds}
            disabled={values.tickmillCompanyId === 0}
            options={availableCountries.map((country: CountryDto) => ({
              id: country.id,
              name: country.name,
            }))}
            error={errors.countryIds as string | undefined}
            showSearch
            allowClear
            onChange={(value) => {
              const nextValue = Array.isArray(value)
                ? value.map(String)
                : value == null
                  ? []
                  : [String(value)]

              setFieldValue('countryIds', nextValue)
            }}
          />
          <FormCheckboxField name='isActive' label='Enabled' />
          <DialogFooter>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button disabled={isSubmitting} key='button' type='button' onClick={handleSubmitForm}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Form>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]
  existingCountryOptions?: BonusCampaignCountryDto[]
  onSubmit(values: AddBonusCountryFormValues): Promise<void>
}

function dataToSubmit(values: AddBonusCountryFormValues): AddBonusCountryFormValues {
  return {
    tickmillCompanyId: values.tickmillCompanyId,
    minimumAmount: Number(values.minimumAmount),
    isActive: values.isActive,
    countryIds: values.countryIds,
  }
}

export const AddBonusCampaignCountryForm = withFormik<OuterProps, AddBonusCountryFormValues>({
  mapPropsToValues: () => ({
    tickmillCompanyId: 0,
    minimumAmount: 0,
    isActive: false,
    countryIds: [],
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddBonusCountryFormValues> = {}
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Tickmill Company is required'
    }
    if (!values.countryIds || values.countryIds.length === 0) {
      errors.countryIds = 'Countries are required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddBonusCountryFormUI)
